import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

type ActiveRole = "manager" | "executive" | "contributor";

interface RolesState {
  isManager: boolean;
  isExecutive: boolean;
  isContributor: boolean;
  loading: boolean;
}

/**
 * Plain role facts for the signed-in user. No stored "active role" and no
 * guessing: the current view is derived from the page the person is on.
 */
export function useUserRoles() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [state, setState] = useState<RolesState>({
    isManager: false,
    isExecutive: false,
    isContributor: false,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (authLoading) return;
      if (!user) {
        setState({ isManager: false, isExecutive: false, isContributor: false, loading: false });
        return;
      }
      try {
        const [{ data: managerRows }, { data: roleRows }, { data: contribRows }] = await Promise.all([
          supabase.from("managers").select("id").eq("user_id", user.id).limit(1),
          supabase.from("user_roles").select("role").eq("user_id", user.id),
          supabase.from("contributor_accounts").select("id").eq("user_id", user.id).limit(1),
        ]);
        // A platform admin in support mode has manager access to that one workspace.
        const { data: support } = await supabase.rpc("admin_my_support" as any);
        if (cancelled) return;
        setState({
          isManager: !!managerRows?.length || !!support,
          isExecutive: !!(roleRows || []).find((r: any) => r.role === "executive"),
          isContributor: !!contribRows?.length,
          loading: false,
        });
      } catch (e) {
        console.error("useUserRoles error:", e);
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading]);

  const path = location.pathname;
  const activeRole: ActiveRole = path.startsWith("/executive")
    ? "executive"
    : path.startsWith("/my-achievements")
      ? "contributor"
      : "manager";

  // Kept for call-site compatibility; navigation itself selects the view.
  const setActiveRole = (_role: ActiveRole) => {};

  return { ...state, activeRole, setActiveRole };
}
